import { useLocalStorage } from 'client/shared/helpers/hooks';
import uuid from 'uuid';
import { privateAdminFromUserAdmin } from '../core/user';
import { AiAssistant, ExtractGql } from 'core';
import {
  AssistantType,
  CurrentAdmin,
  useAdminAiAssistantCreateSession,
  useCurrentAdmin,
} from 'client/shared/graphql-client/graphql-operations.g';
import { useSearchParams } from 'client/shared/hooks';
import { useCallback, useEffect, useState } from 'react';

type GqlPrivateAdmin = ExtractGql<
  NonNullable<NonNullable<CurrentAdmin['currentUser']['user']>['admin']>,
  'PrivateAdmin'
>;

export function useAiAssistantSession(args: { readonly assistant: AiAssistant }) {
  const currentAdminQuery = useCurrentAdmin({
    notifyOnNetworkStatusChange: true, // Required to change loading when variables change
  });
  const [createSession, { loading }] = useAdminAiAssistantCreateSession();
  const privateAdmin = privateAdminFromUserAdmin<GqlPrivateAdmin>(
    currentAdminQuery.data?.currentUser?.user?.admin
  );
  const localStorage = useLocalStorage();
  const searchParams = useSearchParams();
  const sessionIdParam = searchParams.aiSessionId();
  const localStorageKey = `ai-assistant-session-${args.assistant.id}`;

  const [sessionId, setSessionId] = useState<string | null>(
    sessionIdParam ?? localStorage?.getItem(localStorageKey) ?? null
  );

  const createSessionOrResume = useCallback(
    (sessionIdToResume?: string) => {
      createSession({
        variables: {
          sessionId: uuid.v4(),
          assistant: {
            type: args.assistant.type as AssistantType,
            id: args.assistant.id,
          },
          sessionIdToResume,
        },
      })
        .then(({ data }) => {
          if (data?.adminAiAssistantCreateSession) {
            setSessionId(data.adminAiAssistantCreateSession);
          } else {
            throw new Error('Failed to create AI assistant session');
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    [args.assistant, createSession]
  );

  useEffect(() => {
    if (!sessionId && !loading) {
      createSessionOrResume();
    }
  }, [sessionId, loading, createSessionOrResume]);

  useEffect(() => {
    if (sessionId) {
      localStorage?.setItem(localStorageKey, sessionId);
    }
    // TODO: Would be nice to update the URL search param here, but wary of where else this hook is called right now.
    // In the meantime, the URL session ID will confusingly remain the initial value (as it already did).

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return {
    sessionId,
    createSessionOrResume,
    isLoading: currentAdminQuery.loading,
    activePublishingEntity: privateAdmin?.activePublishingEntity,
    isSuperAdmin: privateAdmin?.superAdmin,
  };
}
